import {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactElement,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { t } from "@lingui/core/macro";
import { assocPath } from "ramda";
import { atom, useAtom, useStore, PrimitiveAtom } from "jotai";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { Temporal } from "temporal-polyfill";
import { interpolateTurbo } from "d3-scale-chromatic";

import { useServerFeatureFlag } from "_/data/features";
import {
  useAttemptDatasetManifest,
  useAttemptData,
  Project,
  Attempt,
  usePolledAttempt,
  useProjectAttempts,
  hasAttemptEnded,
  type AttemptDatasetManifest,
} from "_/data/projects";
import { BlobRecord, fetchBlobContentsParallel } from "_/data/blob";
import { imageURL, thumbURL, videoDownloadURL, videoURL } from "_/data/images";
import { useMachineImages, useMachine, type Machine } from "_/data/machines";
import { useIsAonUser } from "_/data/users";

import {
  type SensorPointCloudInfo,
  viewAtom,
  type PrintViewControls,
  glInfoAtom,
  type GLInfo,
} from "_/state/viewer";

import { Slider } from "_/components/slider";
import { Select } from "_/components/select";
import { Checkbox } from "_/components/checkbox";
import {
  SENSOR_VALUE_MIN,
  SENSOR_VALUE_MAX,
  INVALID_SENSOR_TEXTURE_VALUE,
} from "_/components/analyze/sensor-constants";
import { ProgressBar } from "_/components/progress-bar";
import { Spinner } from "_/components/spinner";
import { ViewerOverlayNotice } from "_/components/modal";
import { machineUrls, useAnalyzeRoute } from "_/routes";
import { StatusBadge } from "_/components/badge";
import { TextLink } from "_/components/router";
import { Button } from "_/components/button";
import { Icon } from "_/components/icon";

import { LabeledSlider } from "../sliders";

import {
  useTimer,
  ChunkedAccumulator,
  api,
  ApiResponse,
  normalizeValue,
} from "_/utils";
import { formatHourMinuteDuration } from "_/utils/datetime";

import { Uuid, Triple, Tuple } from "_/types";

import {
  type EncoderData,
  MotionData,
  ForceData,
  PrintDataRecords,
  bufferToRecords,
} from "./parseBin";
import {
  SensorHistogramAccumulator,
  constrainRange,
  getCentralHistogramRange,
  getRangeInsets,
  getVisibleHistogramBins,
  isUsableSensorValue,
  rescaleNestedRange,
  SENSOR_HISTOGRAM_BIN_COUNT,
  zoomRangeAtPosition,
} from "./sensor-histogram";
import {
  type AnalyzeSensorType,
  getAvailableSensorTypes,
  isEncoderSensorType,
  isLabSensorType,
  sensorUnit,
  encoderScalarRecords,
  recordHistogramDomain,
} from "./sensor-type";

import * as S from "./styled";

type ImageData = {
  timestamp: number;
  id: string;
};

type ImageDataWithZ = ImageData & { z: number };

type SensorRenderCacheEntry = {
  histogramDomain: Tuple<number>;
  renderData: ReturnType<typeof constructSensorRenderData>;
  sourceBuffer?: ArrayBuffer;
};

const EMPTY_FORCE_DATA: PrintDataRecords<ForceData> = {
  length: 0,
  at: () => {
    throw new Error("Attempted to access missing force data");
  },
};

type SensorRangeHistogramProps = {
  histogram: Uint32Array;
  histogramDomain: Tuple<number>;
  unit: string;
  fullRange: Tuple<number>;
  valueRange: Tuple<number>;
  colorRange: Tuple<number>;
  onValueRangeChange: (value: Tuple<number>) => void;
  onColorRangeChange: (value: Tuple<number>) => void;
};

type EditableRangeValueProps = {
  label: string;
  precision?: number;
  value: number;
  onCommit: (value: number) => void;
};

const formatRangeValue = (value: number, precision?: number) =>
  precision === undefined
    ? Number(value.toPrecision(6)).toString()
    : value.toFixed(precision);

const EditableRangeValue = ({
  label,
  precision,
  value,
  onCommit,
}: EditableRangeValueProps): ReactElement => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(formatRangeValue(value, precision));

  useEffect(() => {
    if (!editing) setDraft(formatRangeValue(value, precision));
  }, [editing, value, precision]);

  const finishEditing = (commit: boolean) => {
    if (commit) {
      const parsedValue = Number(draft);
      if (Number.isFinite(parsedValue)) {
        onCommit(Math.round(parsedValue * 10) / 10);
      }
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <S.RangeValueInput
        autoFocus
        aria-label={label}
        inputMode="decimal"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onFocus={(event) => event.currentTarget.select()}
        onBlur={() => finishEditing(true)}
        onKeyDown={(event: ReactKeyboardEvent<HTMLInputElement>) => {
          if (event.key === "Enter") finishEditing(true);
          if (event.key === "Escape") finishEditing(false);
        }}
      />
    );
  }

  return (
    <S.RangeValue
      tabIndex={0}
      onClick={() => setEditing(true)}
      onKeyDown={(event: ReactKeyboardEvent<HTMLSpanElement>) => {
        if (event.key === "Enter" || event.key === "F2") setEditing(true);
      }}
    >
      {formatRangeValue(value, precision)}
    </S.RangeValue>
  );
};

const SensorRangeHistogram = ({
  histogram,
  histogramDomain,
  unit,
  fullRange,
  valueRange,
  colorRange,
  onValueRangeChange,
  onColorRangeChange,
}: SensorRangeHistogramProps): ReactElement => {
  const domain = valueRange;
  const domainWidth = domain[1] - domain[0];
  const binWidth =
    (histogramDomain[1] - histogramDomain[0]) / SENSOR_HISTOGRAM_BIN_COUNT;
  const precision = unit === "" ? undefined : unit === "mm" ? 3 : 1;
  const step =
    unit === ""
      ? binWidth / 10
      : unit === "mm"
        ? Math.min(0.001, binWidth)
        : 0.1;
  const unitSuffix = unit ? ` (${unit})` : "";
  const displayedBins = getVisibleHistogramBins(domain, histogramDomain);
  const maxCount = Math.max(
    1,
    ...displayedBins.map(({ bin }) => histogram[bin])
  );
  const maxLogCount = Math.log1p(maxCount);
  const [viewportLeft, viewportRight] = getRangeInsets(fullRange, valueRange);
  const histogramRef = useRef<HTMLDivElement>(null);
  const valueRangeRef = useRef(valueRange);
  const fullRangeRef = useRef(fullRange);
  const onValueRangeChangeRef = useRef(onValueRangeChange);
  const rangeDragRef = useRef<{
    mode: "lower" | "upper" | "pan";
    startX: number;
    startRange: Tuple<number>;
    bounds: DOMRect;
  } | null>(null);

  valueRangeRef.current = valueRange;
  fullRangeRef.current = fullRange;
  onValueRangeChangeRef.current = onValueRangeChange;

  const minimumRangeWidth = Math.min(binWidth, fullRange[1] - fullRange[0]);
  const updateRangeEndpoint = (index: 0 | 1, nextValue: number) => {
    if (index === 0) {
      onValueRangeChange([
        Math.max(
          fullRange[0],
          Math.min(valueRange[1] - minimumRangeWidth, nextValue)
        ),
        valueRange[1],
      ]);
    } else {
      onValueRangeChange([
        valueRange[0],
        Math.min(
          fullRange[1],
          Math.max(valueRange[0] + minimumRangeWidth, nextValue)
        ),
      ]);
    }
  };

  const onOverviewPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const fullWidth = fullRange[1] - fullRange[0];
    const lowerX = ((valueRange[0] - fullRange[0]) / fullWidth) * bounds.width;
    const upperX = ((valueRange[1] - fullRange[0]) / fullWidth) * bounds.width;
    const pointerX = event.clientX - bounds.left;
    const edgeHitWidth = 10;
    let mode: "lower" | "upper" | "pan";

    if (Math.abs(pointerX - lowerX) <= edgeHitWidth) mode = "lower";
    else if (Math.abs(pointerX - upperX) <= edgeHitWidth) mode = "upper";
    else if (pointerX >= lowerX && pointerX <= upperX) mode = "pan";
    else mode = pointerX < lowerX ? "lower" : "upper";

    rangeDragRef.current = {
      mode,
      startX: event.clientX,
      startRange: valueRange,
      bounds,
    };
    event.currentTarget.setPointerCapture(event.pointerId);

    if (pointerX < lowerX || pointerX > upperX) {
      const pointerValue = fullRange[0] + (pointerX / bounds.width) * fullWidth;
      updateRangeEndpoint(mode === "lower" ? 0 : 1, pointerValue);
    }
  };

  const onOverviewPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = rangeDragRef.current;
    if (!drag) return;

    const fullWidth = fullRange[1] - fullRange[0];
    if (drag.mode === "pan") {
      const rangeWidth = drag.startRange[1] - drag.startRange[0];
      const delta =
        ((event.clientX - drag.startX) / drag.bounds.width) * fullWidth;
      const minimum = Math.max(
        fullRange[0],
        Math.min(fullRange[1] - rangeWidth, drag.startRange[0] + delta)
      );
      onValueRangeChange([minimum, minimum + rangeWidth]);
      return;
    }

    const pointerFraction =
      (event.clientX - drag.bounds.left) / drag.bounds.width;
    const pointerValue = fullRange[0] + pointerFraction * fullWidth;
    updateRangeEndpoint(drag.mode === "lower" ? 0 : 1, pointerValue);
  };

  useEffect(() => {
    const histogramElement = histogramRef.current;
    if (!histogramElement) return undefined;

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();

      const bounds = histogramElement.getBoundingClientRect();
      const anchorFraction = (event.clientX - bounds.left) / bounds.width;
      const pixelDelta =
        event.deltaMode === 0
          ? event.deltaY
          : event.deltaMode === 1
            ? event.deltaY * 16
            : event.deltaY * window.innerHeight;
      const boundedDelta = Math.max(-240, Math.min(240, pixelDelta));
      const scale = Math.exp(boundedDelta * 0.0025);

      onValueRangeChangeRef.current(
        zoomRangeAtPosition(
          valueRangeRef.current,
          fullRangeRef.current,
          anchorFraction,
          scale,
          binWidth
        )
      );
    };

    histogramElement.addEventListener("wheel", onWheel, { passive: false });
    return () => histogramElement.removeEventListener("wheel", onWheel);
  }, [binWidth]);

  const minimumColorRangeWidth = Math.min(step, domainWidth);
  const handleColorRangeChange = (nextRange: Tuple<number>) =>
    onColorRangeChange(
      constrainRange(nextRange, valueRange, minimumColorRangeWidth)
    );

  const colorTrackBackground = useMemo(() => {
    const width = Math.max(Number.MIN_VALUE, valueRange[1] - valueRange[0]);
    const displayedColorRange = constrainRange(
      colorRange,
      valueRange,
      minimumColorRangeWidth
    );
    const lower = ((displayedColorRange[0] - valueRange[0]) / width) * 100;
    const upper = ((displayedColorRange[1] - valueRange[0]) / width) * 100;
    const stops = Array.from({ length: 9 }, (_, index) => {
      const fraction = index / 8;
      const position = lower + fraction * (upper - lower);
      return `${interpolateTurbo(fraction)} ${position}%`;
    });
    return `linear-gradient(to right, ${interpolateTurbo(0)} 0%, ${stops.join(
      ", "
    )}, ${interpolateTurbo(1)} 100%)`;
  }, [colorRange, minimumColorRangeWidth, valueRange]);

  const colorTooltipText = (values: Tuple<number>) =>
    values.map((value) =>
      unit
        ? `${value.toFixed(precision)} ${unit}`
        : Number(value.toPrecision(6)).toString()
    );

  return (
    <S.SensorRangeControl>
      <S.RangeLabelRow>
        <span>
          {t`components.controls.analyze.visible-range`}
          {unitSuffix}
        </span>
        <S.RangeValueGroup>
          <EditableRangeValue
            label={t`components.controls.analyze.visible-range-minimum`}
            precision={precision}
            value={valueRange[0]}
            onCommit={(value) => updateRangeEndpoint(0, value)}
          />
          <span>—</span>
          <EditableRangeValue
            label={t`components.controls.analyze.visible-range-maximum`}
            precision={precision}
            value={valueRange[1]}
            onCommit={(value) => updateRangeEndpoint(1, value)}
          />
        </S.RangeValueGroup>
      </S.RangeLabelRow>
      <S.RangePlot>
        <S.FullRangeOverview
          onPointerDown={onOverviewPointerDown}
          onPointerMove={onOverviewPointerMove}
          onPointerUp={() => (rangeDragRef.current = null)}
          onPointerCancel={() => (rangeDragRef.current = null)}
          onLostPointerCapture={() => (rangeDragRef.current = null)}
        >
          <S.FullRangeViewport $left={viewportLeft} $right={viewportRight} />
        </S.FullRangeOverview>

        <S.HistogramFrame ref={histogramRef}>
          <S.HistogramSvg
            viewBox="0 0 1 64"
            preserveAspectRatio="none"
            role="img"
            aria-label={t`components.controls.analyze.sensor-histogram`}
          >
            {displayedBins.map(({ bin, x, width }) => {
              const height = (Math.log1p(histogram[bin]) / maxLogCount) * 62;

              return (
                <rect
                  key={bin}
                  x={x}
                  y={64 - height}
                  width={width}
                  height={height}
                  opacity={0.85}
                />
              );
            })}
          </S.HistogramSvg>
        </S.HistogramFrame>

        <S.ColorRangeRail>
          <Slider
            min={valueRange[0]}
            max={valueRange[1]}
            step={step}
            value={colorRange}
            trackBackground={colorTrackBackground}
            rangeBackground="transparent"
            thumbColor="currentColor"
            thumbVisualWidth={4}
            tooltipPlacement="after"
            tooltipText={colorTooltipText}
            onChange={handleColorRangeChange}
          />
        </S.ColorRangeRail>
      </S.RangePlot>
      <S.RangeLabelRow>
        <span>
          {t`components.controls.analyze.color-range`}
          {unitSuffix}
        </span>
        <S.RangeValueGroup>
          <EditableRangeValue
            label={t`components.controls.analyze.color-range-minimum`}
            precision={precision}
            value={colorRange[0]}
            onCommit={(value) =>
              handleColorRangeChange([
                Math.max(valueRange[0], Math.min(colorRange[1], value)),
                colorRange[1],
              ])
            }
          />
          <span>—</span>
          <EditableRangeValue
            label={t`components.controls.analyze.color-range-maximum`}
            precision={precision}
            value={colorRange[1]}
            onCommit={(value) =>
              handleColorRangeChange([
                colorRange[0],
                Math.min(valueRange[1], Math.max(colorRange[0], value)),
              ])
            }
          />
        </S.RangeValueGroup>
      </S.RangeLabelRow>
    </S.SensorRangeControl>
  );
};

// Creates the geometry buffer attribute arrays from the motion and sensor data
function constructSensorRenderData(
  motionData: PrintDataRecords<MotionData>,
  forceData: PrintDataRecords<ForceData>[],
  glInfo: GLInfo,
  histogramDomain: Tuple<number> = [SENSOR_VALUE_MIN, SENSOR_VALUE_MAX],
  normalizeValues = false
): {
  startPoints: Float32Array;
  endPoints: Float32Array;
  sensorValues: Uint16Array;
  sensorTextureDimensions: Triple<number>;
  offsets: Int32Array;
  counts: Int32Array;
  extrusionValues: Int32Array;
  segmentTools: Uint8Array;
  info: SensorPointCloudInfo;
} {
  const SENSOR_ARRAY_CHUNK_SIZE = 1_000_000;

  // Sensor values are stored as WebGL half floats (`HalfFloatType`) so the
  // array texture allocates 2 bytes per texel instead of 4, halving the total
  // allocation. Large prints otherwise exceed the driver's per-texture limit
  // which seems to be a total of 1GB of RAM allocated (`GL_INVALID_OPERATION:
  // glTexStorage3D: ... allocation size is too large`). `Float16Array` does the
  // float-to-half conversion natively on assignment.
  let sensorValueChunks: ChunkedAccumulator<Float16Array> | null =
    new ChunkedAccumulator(SENSOR_ARRAY_CHUNK_SIZE, Float16Array);

  let startPointsPartial: number[] = [];
  let endPointsPartial: number[] = [];
  let offsetsPartial: number[] = [];
  let countsPartial: number[] = [];
  let extrusionValuesPartial: number[] = [];
  const segmentToolsPartial: number[] = [];

  const toolForceDataIndices = Array.from(
    { length: forceData.length },
    () => 0
  );

  let minZ = Infinity;
  let maxZ = -Infinity;

  let minExtrudedZ = Infinity;
  let maxExtrudedZ = -Infinity;

  const isUsableValue = normalizeValues ? Number.isFinite : isUsableSensorValue;
  const sensorHistogram = new SensorHistogramAccumulator(
    histogramDomain,
    isUsableValue
  );
  const extrudedSensorHistogram = new SensorHistogramAccumulator(
    histogramDomain,
    isUsableValue
  );

  // Z-jump filter state: tracks the highest Z seen in any valid extruding segment
  // and the Z of the most recent valid extruding segment.
  let highestValidZ = -Infinity;
  let lastValidExtrudingZ: number | null = null;

  // Collect all the force data points that occur before the next motion data point and after the previous one.
  for (let i = 1; i < motionData.length; i++) {
    const start = motionData.at(i - 1);
    const end = motionData.at(i);

    // Jump ahead to first force data point for the segment.
    forceData.forEach((toolForceData, j) => {
      const len = toolForceData.length;
      while (
        toolForceDataIndices[j] < len &&
        toolForceData.at(toolForceDataIndices[j]).timestamp < start.timestamp
      ) {
        toolForceDataIndices[j]++;
      }
    });

    // If the tool is the same at the start and end of the segment, we consider the segment to be a movement of this tool.
    if (start.tool === end.tool) {
      const activeTool = start.tool;

      // Interpolate data points until we reach the end of the segment.
      const activeToolForceData = forceData[activeTool];
      const len = activeToolForceData.length;

      let dataPoint: ForceData;

      // Check whether some non-zero xy movement occurs in the segment
      const xyMovement = start.x !== end.x || start.y !== end.y;

      const z = start.z;

      // Check whether the segment is on or above the z=0 plane (the bed surface)
      const nonNegativeZ = z >= 0 && end.z >= 0;

      // If there is either no xy movement or the z value is negative, convert extruding segments
      // to non-extruding ones in order to hide them from the extrusion visualization
      let e = !(xyMovement && nonNegativeZ) && start.e > 0 ? -1 : start.e;

      // Z-jump filter and nontrivial-Z filter: reject extruding moves that appear at an
      // implausible Z height or are diagonal transition moves rather than planar extrusion.
      // Valid range is [-5, max(highestValidZ, 5)]; additionally any jump of more than
      // 5 mm from the last accepted extruding Z is rejected regardless of absolute height.
      if (e > 0) {
        const dxy = Math.hypot(end.x - start.x, end.y - start.y);
        const dz = Math.abs(end.z - start.z);
        const hasNontrivialZ = dz > 0.005 * dxy;

        const validZUpper = Math.max(highestValidZ + 5, 5);
        const inValidWindow = z >= -5 && z <= validZUpper;
        const isJump =
          lastValidExtrudingZ !== null && Math.abs(z - lastValidExtrudingZ) > 5;
        if (hasNontrivialZ || !inValidWindow || isJump) {
          e = 0;
        } else {
          highestValidZ = Math.max(highestValidZ, z);
          lastValidExtrudingZ = z;
        }
      }

      const segmentSensorValues: number[] = [];
      let hasUsableSegmentValue = false;

      while (
        toolForceDataIndices[activeTool] < len &&
        (dataPoint = activeToolForceData.at(toolForceDataIndices[activeTool]))
          .timestamp < end.timestamp
      ) {
        const sensorValue = dataPoint.sensorValue;
        const isUsable = isUsableValue(sensorValue);

        // Preserve one texture entry per timestamp so rejecting a device
        // sentinel does not redistribute later samples along the segment. The
        // shader discards the sentinel placeholder before rendering it.
        const textureValue = normalizeValues
          ? normalizeValue(sensorValue, histogramDomain)
          : sensorValue;
        segmentSensorValues.push(
          isUsable ? textureValue : INVALID_SENSOR_TEXTURE_VALUE
        );

        if (isUsable) {
          hasUsableSegmentValue = true;

          // Update value range with the current data point.
          sensorHistogram.add(sensorValue);

          // Update the extruded sensor value range if the segment is extruded.
          if (e > 0) {
            extrudedSensorHistogram.add(sensorValue);
          }
        }

        toolForceDataIndices[activeTool]++;
      }

      const numSegmentPoints = segmentSensorValues.length;

      // A segment containing only rejected samples has nothing to render.
      if (!hasUsableSegmentValue) {
        continue;
      }

      // Update z range the current segment.
      minZ = Math.min(z, minZ);
      maxZ = Math.max(z, maxZ);

      // Update the extruded z range if the segment is extruded.
      if (e > 0) {
        minExtrudedZ = Math.min(z, minExtrudedZ);
        maxExtrudedZ = Math.max(z, maxExtrudedZ);
      }

      for (let pointIndex = 0; pointIndex < numSegmentPoints; pointIndex++) {
        sensorValueChunks.push(segmentSensorValues[pointIndex]);
      }

      startPointsPartial.push(start.x, start.y, start.z);

      endPointsPartial.push(end.x, end.y, end.z);

      extrusionValuesPartial.push(e);
      segmentToolsPartial.push(activeTool);

      const offset =
        offsetsPartial.length === 0
          ? 0
          : offsetsPartial[offsetsPartial.length - 1] +
            countsPartial[countsPartial.length - 1];

      countsPartial.push(numSegmentPoints);
      offsetsPartial.push(offset);
    }
  }

  const numSensorValues = sensorValueChunks.length;

  const { maxTextureSize } = glInfo;

  const maxTextureSizeSquared = maxTextureSize * maxTextureSize;

  // Find the minimum number of texture layers needed to store all the sensor data points.
  const numTextureLayers = Math.max(
    1,
    Math.ceil(numSensorValues / maxTextureSizeSquared)
  );

  // Find the target size of each texture layer so that the data is evenly distributed
  // across the layers.
  const targetLayerSize = Math.max(
    1,
    Math.ceil(numSensorValues / numTextureLayers)
  );

  // Find the dimensions of the sensor data texture so that the texture is close to square.
  const sensorTextureHeight = Math.floor(Math.sqrt(targetLayerSize));

  const sensorTextureWidth = Math.ceil(targetLayerSize / sensorTextureHeight);

  const sensorHalfFloats = sensorValueChunks.getData(
    sensorTextureWidth * sensorTextureHeight * numTextureLayers
  );

  const fullSensorValueRange = sensorHistogram.getFullRange();
  const fullExtrudedSensorValueRange = extrudedSensorHistogram.getFullRange();
  const sensorValueRange = fullSensorValueRange;
  const extrudedSensorValueRange = fullExtrudedSensorValueRange;

  sensorValueChunks = null;

  // A `HalfFloatType` texture stores each texel as a raw 16-bit half-float bit
  // pattern that is encoded into a `Uint16Array`, because this support was
  // added to WebGL long before JS had `Float16Array`. But the underlying data
  // really is a half-float, and the shader reads it as such. So we hand the
  // texture a `Uint16Array` view over the same bytes: a zero-copy reinterpret
  // of the bit patterns, not a numeric conversion. This is required, not
  // stylistic: WebGL2 rejects a `Float16Array` passed directly for a
  // `HALF_FLOAT` upload ("type HALF_FLOAT_OES but ArrayBufferView is not ...
  // Uint16Array"); only a `Uint16Array` of the patterns is accepted.
  //
  // For this reason, be careful when using the data in this buffer, because its
  // contents are not meaningful as integers without transformation.
  const sensorValues = new Uint16Array(sensorHalfFloats.buffer);

  const startPoints = new Float32Array(startPointsPartial);
  startPointsPartial = [];

  const endPoints = new Float32Array(endPointsPartial);
  endPointsPartial = [];

  const offsets = new Int32Array(offsetsPartial);
  offsetsPartial = [];

  const counts = new Int32Array(countsPartial);
  countsPartial = [];

  const extrusionValues = new Int32Array(extrusionValuesPartial);
  extrusionValuesPartial = [];

  return {
    startPoints,
    endPoints,
    sensorValues,
    sensorTextureDimensions: [
      sensorTextureWidth,
      sensorTextureHeight,
      numTextureLayers,
    ],
    offsets,
    counts,
    extrusionValues,
    segmentTools: new Uint8Array(segmentToolsPartial),
    info: {
      sensorTextureDomain: normalizeValues ? histogramDomain : undefined,
      zRange: [minZ, maxZ],
      extrudedZRange: [
        minExtrudedZ === Infinity ? minZ : minExtrudedZ,
        maxExtrudedZ === -Infinity ? maxZ : maxExtrudedZ,
      ], // If there are no extruded points, use the regular z range
      sensorValueRange,
      extrudedSensorValueRange,
      fullSensorValueRange,
      fullExtrudedSensorValueRange,
      sensorHistogram: sensorHistogram.bins,
      extrudedSensorHistogram: extrudedSensorHistogram.bins,
    },
  };
}

function interpolateZValuesForImageTimestamps(
  motionData: PrintDataRecords<MotionData>,
  imageData: ImageData[]
) {
  // Add z values to the image data by interpolating between the motion data points
  const imageDataWithZ: ImageDataWithZ[] = [];

  if (motionData.length === 0) {
    return imageDataWithZ;
  }

  let motionIndex = 0;

  imageData.forEach((image) => {
    while (
      motionData.at(motionIndex).timestamp < image.timestamp &&
      motionIndex < motionData.length - 1
    ) {
      motionIndex++;
    }

    // If the image is before the first motion data point, use the first motion data point's z value.
    if (motionIndex === 0) {
      imageDataWithZ.push({ ...image, z: motionData.at(0).z });
    } else {
      const start = motionData.at(motionIndex - 1);
      const end = motionData.at(motionIndex);

      const ts =
        (image.timestamp - start.timestamp) / (end.timestamp - start.timestamp);

      const interpolatedZ = start.z + (end.z - start.z) * ts;

      imageDataWithZ.push({ ...image, z: interpolatedZ });
    }
  });

  return imageDataWithZ;
}

export function binarySearchImageData(
  imageDataWithZ: ImageDataWithZ[],
  z: number
): ImageDataWithZ | null {
  const numImages = imageDataWithZ.length;

  let left = 0;
  let right = numImages - 1;

  // To start, assume the result is the last image. If there are no images, the result is null.
  let result = numImages > 0 ? imageDataWithZ[right] : null;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midZ = imageDataWithZ[mid].z;

    // If the mid point is greater than or equal to the z value, it is the current candidate for the result.
    if (midZ >= z) {
      result = imageDataWithZ[mid];
      right = mid - 1;
    } else {
      left = mid + 1;
    }
  }

  return result;
}

type SensorDataControlsProps = {
  statusTimestamp: string;
  /**
   * Jotai atom to access the control state for the sensor data visualization.
   */
  controlsAtom: PrimitiveAtom<PrintViewControls>;
  sensorTypes: ReturnType<typeof getAvailableSensorTypes>;
  sensorType: AnalyzeSensorType;
  histogramDomain: Tuple<number>;
  onSensorTypeChange: (type: AnalyzeSensorType) => void;
  highPassEncoders: boolean;
  onHighPassEncodersChange: (enabled: boolean) => void;

  /**
   * Attempt ID for the current sensor data visualization.
   */
  attempt: Attempt;
  /**
   * Image data with interpolated z values.
   */
  imageDataWithZ: ImageDataWithZ[];

  /**
   * Timelapse ID associated with the attempt.
   */
  timelapseId: Uuid | undefined;

  /**
   * Set to true if the attempt was completed recently, but its
   * timelapse video isn't available yet.
   */
  timelapseGenerating: boolean;

  /**
   * Callback to refresh data for this attempt. Useful while the
   * print is running.
   */
  refreshData: () => void;

  /**
   * Refresh the attempt record without reloading its motion or force data.
   * Exception: if the attempt just ended, then its final data will be reloaded.
   */
  refreshAttempt: () => Promise<void>;

  /**
   * Reports when the stop/pause modal opens or closes so automatic dataset
   * refreshes (when the attempt ends) can be deferred until it closes.
   */
  onCommandModalOpenChange: (isOpen: boolean) => void;

  /**
   * Machine associated with the attempt.
   */
  machine: Machine;
};

/**
 * Renders controls for the sensor data point cloud.
 */
const SensorDataControls = ({
  controlsAtom,
  sensorTypes,
  sensorType,
  histogramDomain,
  onSensorTypeChange,
  highPassEncoders,
  onHighPassEncodersChange,
  attempt,
  imageDataWithZ,
  refreshData,
  refreshAttempt,
  onCommandModalOpenChange,
  timelapseId,
  timelapseGenerating,
  machine,
  statusTimestamp,
}: SensorDataControlsProps): ReactElement => {
  const [controls, setControls] = useAtom(controlsAtom);
  const attemptDuration = Temporal.Instant.from(attempt.startedAt).until(
    Temporal.Instant.from(attempt.endedAt ?? statusTimestamp)
  );

  // Most recently-stored update to the layer slider's z value to used for image lookup
  const [imageLookupZValue, setImageLookupZValue] = useState<number | null>(
    null
  );

  // Timer hook to debounce the image lookup based on the layer slider value changing
  const [zTimerFinished, resetZTimer] = useTimer(200);

  const [image, setImage] = useState<ImageDataWithZ | null>(
    imageDataWithZ.length > 0 ? imageDataWithZ[imageDataWithZ.length - 1] : null
  );

  // Find the image with the closest greater z value to the current z value
  useEffect(() => {
    if (
      zTimerFinished &&
      imageDataWithZ.length > 0 &&
      imageLookupZValue != null
    ) {
      const imageData = binarySearchImageData(
        imageDataWithZ,
        imageLookupZValue
      );
      if (imageData) {
        setImage(imageData);
      }
    }
  }, [imageDataWithZ, imageLookupZValue, zTimerFinished]);

  const { zRange, valueRange, colorRange, lineWidth, showNonExtrusionMotion } =
    controls;

  const selectedSuggestedRange = showNonExtrusionMotion
    ? valueRange.suggestedRange
    : valueRange.extrudedSuggestedRange;
  const selectedHistogram = showNonExtrusionMotion
    ? valueRange.histogram
    : valueRange.extrudedHistogram;
  const selectedFullRange = showNonExtrusionMotion
    ? valueRange.fullRange
    : valueRange.fullExtrudedRange;

  // Reset the visible range when switching between extrusion-only and all
  // motion. The histogram domain follows this range continuously.
  useEffect(() => {
    setControls(assocPath(["valueRange", "value"], selectedSuggestedRange));
  }, [showNonExtrusionMotion, selectedSuggestedRange, setControls]);

  const previousValueRangeRef = useRef(valueRange.value);

  // Preserve explicitly selected color values as the histogram viewport
  // changes. An endpoint follows the viewport only while it is pinned to the
  // corresponding edge, and otherwise moves only when it must be clamped into
  // the visible range.
  useEffect(() => {
    const previousValueRange = previousValueRangeRef.current;
    const nextValueRange = valueRange.value;
    previousValueRangeRef.current = nextValueRange;

    if (
      previousValueRange[0] === nextValueRange[0] &&
      previousValueRange[1] === nextValueRange[1]
    ) {
      return;
    }

    setControls((currentControls) => {
      const nextColorRange = constrainRange(
        rescaleNestedRange(
          currentControls.colorRange.value,
          previousValueRange,
          nextValueRange
        ),
        nextValueRange,
        Math.min(
          isLabSensorType(sensorType)
            ? (histogramDomain[1] - histogramDomain[0]) /
                SENSOR_HISTOGRAM_BIN_COUNT
            : sensorType === "toolForce"
              ? 0.1
              : 0.0001,
          nextValueRange[1] - nextValueRange[0]
        )
      );

      return assocPath(
        ["colorRange", "value"],
        nextColorRange,
        currentControls
      );
    });
  }, [setControls, valueRange.value, sensorType, histogramDomain]);

  const handleIncrementImage = (delta: number) => {
    if (!image) return;
    const currentIndex = imageDataWithZ.indexOf(image);
    if (currentIndex === -1) return;
    const newIndex = Math.max(
      0,
      Math.min(imageDataWithZ.length - 1, currentIndex + delta)
    );
    const newImage = imageDataWithZ[newIndex];
    setImage(newImage);
    setControls(assocPath(["zRange", "value", 1], newImage.z));
    setImageLookupZValue(newImage.z);
  };

  const layerSliderRange = showNonExtrusionMotion
    ? zRange.range
    : zRange.extrudedRange;

  return (
    <>
      <S.LayerControlContainer>
        <S.ControlLabel>{t`common.layer`}</S.ControlLabel>
        <S.LayerSliderContainer>
          <Slider
            min={layerSliderRange[0]}
            max={layerSliderRange[1]}
            step={0.1}
            value={zRange.value}
            size="large"
            vertical
            tooltipPlacement="before"
            tooltipText={(values) => values.map((v) => `Z: ${v.toFixed(2)}mm`)}
            onChange={(values) => {
              setImageLookupZValue((value) => {
                if (value === values[1]) {
                  return value;
                } else {
                  if (zTimerFinished) {
                    resetZTimer();
                  }
                  return values[1];
                }
              });

              setControls(assocPath(["zRange", "value"], values));
            }}
          />
        </S.LayerSliderContainer>
      </S.LayerControlContainer>
      <S.RightSideContainer>
        <S.PointCloudControlContainer>
          <S.ProjectInfoItem>
            <S.ProjectInfoItemColumns>
              <S.ProjectInfoLabel>
                {t`common.progress`}
                {!attempt.endedAt && (
                  <Button
                    title={t`components.controls.analyze.refresh-print-data`}
                    variant="text"
                    size="large"
                    iconOnly
                    onClick={refreshData}
                  >
                    <Icon variant="Refresh" />
                  </Button>
                )}
              </S.ProjectInfoLabel>
              <StatusBadge status={attempt.status} />
            </S.ProjectInfoItemColumns>
            <S.ProjectInfoValue>
              <span>{formatHourMinuteDuration(attemptDuration)}</span>
              <S.StopPauseButtons
                machine={machine}
                attempt={attempt}
                refreshAttempt={refreshAttempt}
                onModalOpenChange={onCommandModalOpenChange}
              />
            </S.ProjectInfoValue>
          </S.ProjectInfoItem>
          <S.ProjectInfoItem>
            <S.ProjectInfoLabel>{t`common.machine`}</S.ProjectInfoLabel>
            <S.ProjectInfoValue>
              <TextLink variant="default" to={machineUrls.index(machine.id)}>
                {machine.name}
              </TextLink>
            </S.ProjectInfoValue>
          </S.ProjectInfoItem>
        </S.PointCloudControlContainer>
        {image != null && (
          <S.MachineCameraMediaContainer>
            <S.MachineCameraMediaInnerWrapper>
              <S.MachineCameraMedia
                src={imageURL(image.id)}
                thumb={thumbURL(image.id)}
                video={timelapseId ? videoURL(timelapseId) : undefined}
                videoDownload={
                  timelapseId ? videoDownloadURL(timelapseId) : undefined
                }
                onIncrementImage={handleIncrementImage}
              />
            </S.MachineCameraMediaInnerWrapper>
            {timelapseGenerating && (
              <S.GeneratingTimelapseMessage>
                {t`components.controls.analyze.generating-timelapse`}
              </S.GeneratingTimelapseMessage>
            )}
          </S.MachineCameraMediaContainer>
        )}
        <S.PointCloudControlContainer>
          {sensorTypes.length > 1 && (
            <Select
              label={t`components.controls.analyze.sensor-stream`}
              options={sensorTypes.map((option) => ({
                label: option.label,
                value: option.value,
                data: option.value,
              }))}
              value={sensorType}
              onChange={(type) => {
                if (type) onSensorTypeChange(type);
              }}
            />
          )}
          <SensorRangeHistogram
            histogram={selectedHistogram}
            histogramDomain={histogramDomain}
            unit={sensorUnit(sensorType)}
            fullRange={selectedFullRange}
            valueRange={valueRange.value}
            colorRange={colorRange.value}
            onValueRangeChange={(values) => {
              setControls(assocPath(["valueRange", "value"], values));
            }}
            onColorRangeChange={(values) => {
              setControls(assocPath(["colorRange", "value"], values));
            }}
          />
          <LabeledSlider
            label={t`components.controls.analyze.line-width`}
            min={lineWidth.range[0]}
            max={lineWidth.range[1]}
            step={0.005}
            value={lineWidth.value}
            thumbColor="currentColor"
            thumbVisualWidth={4}
            onChange={(value) => {
              setControls(assocPath(["lineWidth", "value"], value));
            }}
          />
          {isEncoderSensorType(sensorType) && (
            <Checkbox
              label={t`components.controls.analyze.high-pass-encoders`}
              checked={highPassEncoders}
              onChange={onHighPassEncodersChange}
            />
          )}
        </S.PointCloudControlContainer>
      </S.RightSideContainer>
    </>
  );
};

type PrintAttemptLoaderProps = {
  /**
   * Uuid of associated project.
   */
  projectId: Uuid;

  /**
   * Uuid of attempt to display.
   */
  attempt: Attempt;

  /**
   * Uuid of motion data dataset to load and display.
   */
  motionDataId: Uuid;

  /**
   * Uuid of tool force 0 dataset to load and display.
   */
  toolForce0Id: Uuid | undefined;

  /**
   * Uuid of tool force 1 dataset to load and display.
   */
  toolForce1Id: Uuid | undefined;
  encoderDataId: Uuid | undefined;
  manifest: AttemptDatasetManifest[];
  additionalSensorsEnabled: boolean;

  /**
   * Timelapse ID for the attempt, used to determine whether to show video UI in
   * the photo widget.
   */
  timelapseId: Uuid | undefined;

  /**
   * Set to true if the attempt was completed recently, but its
   * timelapse video isn't available yet.
   */
  timelapseGenerating: boolean;

  /**
   * Timestamp for when data fetching should stop. When prints are complete,
   * this will be the same as `attempt.endedAt`, but for ongoing prints this
   * will be the last time that data was loaded. Because data loading can take
   * many seconds, we don't refresh automatically. Instead, the user must cause
   * an action to refresh data, which will cause `endTimestamp` to be updated by
   * the caller.
   */
  endTimestamp: string;

  /**
   * Timestamp of the latest attempt-status refresh. Advances with polling so
   * elapsed print time and stale-data age update without reloading the large
   * print datasets.
   */
  statusTimestamp: string;

  /**
   * Callback to refresh data for the attempt. Useful while the print is running.
   */
  refreshData: () => void;

  /**
   * Refresh the attempt record without reloading motion or force data, except
   * if the attempt has ended.  In that case force & motion data is reloaded.
   */
  refreshAttempt: () => Promise<void>;

  /**
   * Reports when the stop/pause modal opens or closes so automatic dataset
   * refreshes (when the attempt ends) can be deferred until it closes.
   */
  onCommandModalOpenChange: (isOpen: boolean) => void;
};

// Loads the data binaries, constructs point cloud arrays and control layout to render the PrintView component,
// and sets the viewAtom to the PrintView
const PrintAttemptLoader = ({
  statusTimestamp,
  projectId,
  attempt,
  motionDataId,
  toolForce0Id,
  toolForce1Id,
  encoderDataId,
  manifest,
  additionalSensorsEnabled,
  timelapseId,
  timelapseGenerating,
  endTimestamp,
  refreshData,
  refreshAttempt,
  onCommandModalOpenChange,
}: PrintAttemptLoaderProps) => {
  const [glInfo] = useAtom(glInfoAtom);

  const [_view, setView] = useAtom(viewAtom);
  const atomStore = useStore();
  const queryClient = useQueryClient();

  const { data: machine } = useMachine(attempt.machineId);

  const [controlsAtom, setControlsAtom] =
    useState<PrimitiveAtom<PrintViewControls>>();
  const sensorTypes = getAvailableSensorTypes(
    manifest,
    additionalSensorsEnabled
  );
  const [selectedSensorType, setSensorType] =
    useState<AnalyzeSensorType>("toolForce");
  const sensorType = sensorTypes.some(
    (option) => option.value === selectedSensorType
  )
    ? selectedSensorType
    : sensorTypes[0].value;
  const selectedSensor = sensorTypes.find(
    (sensor) => sensor.value === sensorType
  );
  const [histogramDomain, setHistogramDomain] = useState<Tuple<number>>([
    SENSOR_VALUE_MIN,
    SENSOR_VALUE_MAX,
  ]);
  const [encoderProgress, setEncoderProgress] = useState(0);
  const [highPassEncoders, setHighPassEncoders] = useState(true);

  const [motionDataProgress, setMotionDataProgress] = useState(0);
  const [toolForce0Progress, setToolForce0Progress] = useState(0);
  const [toolForce1Progress, setToolForce1Progress] = useState(0);
  const [imageDataWithZ, setImageDataWithZ] = useState<ImageDataWithZ[]>([]);

  const hasToolForce0 = Boolean(toolForce0Id);
  const hasToolForce1 = Boolean(toolForce1Id);
  const refetchDataOnMount = hasAttemptEnded(attempt.status) ? "always" : false;

  const motionDataQueryResult = useAttemptData(
    projectId,
    attempt.id,
    motionDataId,
    setMotionDataProgress,
    { refetchOnMount: refetchDataOnMount }
  );
  const { data: motionDataBuffer } = motionDataQueryResult;

  // Load only the selected stream. React Query retains visited datasets.
  type SensorDatasetDescriptor = {
    label: string;
    id: Uuid;
    setProgress: React.Dispatch<SetStateAction<number>>;
  };
  const sensorDescriptors = [
    {
      label: "axesEncoders",
      id: encoderDataId,
      setProgress: setEncoderProgress,
    } as const,
    {
      label: "toolForce0",
      id: toolForce0Id,
      setProgress: setToolForce0Progress,
    } as const,
    {
      label: "toolForce1",
      id: toolForce1Id,
      setProgress: setToolForce1Progress,
    } as const,
    ...manifest
      .filter(
        (dataset) =>
          additionalSensorsEnabled &&
          dataset.samples > 0 &&
          isLabSensorType(dataset.kind)
      )
      .map((dataset) => ({
        label: dataset.kind,
        id: dataset.id,
        setProgress: setEncoderProgress,
      })),
  ].filter((tf): tf is SensorDatasetDescriptor => Boolean(tf.id));

  const sensorQueries = useQueries({
    queries: sensorDescriptors.map(({ id, label, setProgress }) => ({
      queryKey: ["attemptSensorData", projectId, attempt.id, label, id],
      enabled: selectedSensor?.datasetKinds.includes(label) ?? false,
      queryFn: async ({
        signal,
      }: {
        signal?: AbortSignal;
      }): Promise<ArrayBuffer | undefined> => {
        if (!id) return undefined;
        const blobRecordResponse = await api.get<ApiResponse<BlobRecord>>(
          `/blobs/${id}`
        );
        const blobRecord = blobRecordResponse.data.data;
        return fetchBlobContentsParallel(
          blobRecord,
          (blobId: Uuid) =>
            `/projects/${projectId}/attempts/${attempt.id}/data/${blobId}`,
          setProgress,
          signal
        );
      },
      staleTime: Infinity,
      refetchOnMount: refetchDataOnMount,
    })),
  });

  const imageDataArray = useAttemptImages(attempt, endTimestamp);

  const prevEndTimestampRef = useRef<string>(endTimestamp);
  useEffect(() => {
    if (prevEndTimestampRef.current !== endTimestamp) {
      setMotionDataProgress(0);
      setToolForce0Progress(0);
      setToolForce1Progress(0);

      // Update the end timestamp, which will also cause images to be refetched.
      prevEndTimestampRef.current = endTimestamp;

      // Mark inactive streams stale too, so they load fresh data when selected.
      // Stable keys preserve cached data when revisiting an ongoing print.
      void queryClient.invalidateQueries({
        queryKey: ["attemptSensorData", projectId, attempt.id],
      });
      setEncoderProgress(0);
      void motionDataQueryResult.refetch();
    }
    // Query changes fetch their own datasets; refresh here only when the
    // selected time window advances.
    //
    // react-query `refetch` functions are not required in the dependency array,
    // and including them causes unnecessary re-renders. When we upgrade to V5
    // they will have stable identity so we can safely put them into a
    // dependency array, but for now we can disable the lint warning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endTimestamp]);

  const isDataFetching =
    sensorQueries.some((q) => q.isEnabled && q.isFetching) ||
    motionDataQueryResult.isFetching;

  const sensorQueryByLabel = Object.fromEntries(
    sensorDescriptors.map((tf, i) => [tf.label, sensorQueries[i]])
  );

  const toolForce0Query = sensorQueryByLabel["toolForce0"];
  const toolForce1Query = sensorQueryByLabel["toolForce1"];
  const encoderQuery = sensorQueryByLabel["axesEncoders"];
  const labQuery = isLabSensorType(sensorType)
    ? sensorQueryByLabel[sensorType]
    : undefined;
  const labBuffer = labQuery?.data;
  const encoderBuffer = isEncoderSensorType(sensorType)
    ? encoderQuery?.data
    : undefined;
  // Keep visited streams until the downloaded recording changes.
  const encoderRecordsCache = useMemo(
    () => new Map<string, PrintDataRecords<ForceData>>(),
    // Source identity deliberately invalidates the cache.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [encoderQuery?.data]
  );
  const encoderScalars = useMemo(() => {
    if (!encoderBuffer || !isEncoderSensorType(sensorType)) return undefined;
    const key = `${sensorType}:${highPassEncoders}`;
    const cached = encoderRecordsCache.get(key);
    if (cached) return cached;
    const records = encoderScalarRecords(
      bufferToRecords<EncoderData>(encoderBuffer),
      sensorType,
      highPassEncoders
    );
    encoderRecordsCache.set(key, records);
    return records;
  }, [encoderBuffer, sensorType, highPassEncoders, encoderRecordsCache]);
  const labRecords = useMemo(
    () => (labBuffer ? bufferToRecords<ForceData>(labBuffer) : undefined),
    [labBuffer]
  );
  const scalarRecords = labRecords ?? encoderScalars;

  const toolForce0Buffer = toolForce0Query?.data;
  const toolForce1Buffer = toolForce1Query?.data;
  // The histogram scan and motion interpolation are also expensive. Retain
  // completed views, but discard them when their source data or GL limits change.
  const sensorRenderCacheRef = useRef(
    new Map<string, SensorRenderCacheEntry>()
  );

  // Invalidate before the effect below rebuilds the selected view.
  useEffect(() => {
    sensorRenderCacheRef.current.clear();
  }, [
    motionDataBuffer,
    encoderQuery?.data,
    toolForce0Buffer,
    toolForce1Buffer,
    glInfo,
  ]);

  useEffect(() => {
    if (motionDataQueryResult.isFetched) {
      setMotionDataProgress(1);
    }
  }, [motionDataQueryResult.isFetched]);

  useEffect(() => {
    // If there's nothing to fetch for this toolhead, treat it as "done" so the UI doesn't hang.
    if (!hasToolForce0 || toolForce0Query?.isFetched) {
      setToolForce0Progress(1);
    }
  }, [hasToolForce0, toolForce0Query?.isFetched]);

  useEffect(() => {
    // Same idea for toolhead 1: no dataset means no waiting.
    if (!hasToolForce1 || toolForce1Query?.isFetched) {
      setToolForce1Progress(1);
    }
  }, [hasToolForce1, toolForce1Query?.isFetched]);

  let loadingError = Boolean(motionDataQueryResult.error);

  if (sensorType === "toolForce") {
    loadingError ||= Boolean(toolForce0Query?.error);
    loadingError ||= Boolean(toolForce1Query?.error);
  } else if (isEncoderSensorType(sensorType)) {
    loadingError ||= Boolean(encoderQuery?.error);
  } else if (isLabSensorType(sensorType)) {
    loadingError ||= Boolean(labQuery?.error);
  }

  const toolForceBars = [
    {
      title: t`components.controls.analyze.tool-force ${0}`,
      show: sensorType === "toolForce" && hasToolForce0,
      value: toolForce0Progress,
    },
    {
      title: t`components.controls.analyze.tool-force ${1}`,
      show: sensorType === "toolForce" && hasToolForce1,
      value: toolForce1Progress,
    },
  ];

  useEffect(() => {
    // clear the controls atom when the data changes so that the controls are reset
    setControlsAtom(undefined);

    if (!glInfo) {
      console.error("GLInfo not available");
      return;
    }

    const needsToolForce0 = hasToolForce0 && !toolForce0Buffer;
    const needsToolForce1 = hasToolForce1 && !toolForce1Buffer;

    // Wait for motion data and the selected sensor's datasets.
    if (
      !motionDataBuffer ||
      (sensorType === "toolForce" && (needsToolForce0 || needsToolForce1)) ||
      (isEncoderSensorType(sensorType) && !encoderBuffer) ||
      (isLabSensorType(sensorType) && !labBuffer)
    ) {
      return;
    }

    const motionBin = bufferToRecords<MotionData>(motionDataBuffer);
    const forceRecords: PrintDataRecords<ForceData>[] = [
      toolForce0Buffer
        ? bufferToRecords<ForceData>(toolForce0Buffer)
        : EMPTY_FORCE_DATA,
      toolForce1Buffer
        ? bufferToRecords<ForceData>(toolForce1Buffer)
        : EMPTY_FORCE_DATA,
    ];
    // Machine-wide scalars share samples across tools, with separate cursors.
    const toolForceRecords = scalarRecords
      ? [scalarRecords, scalarRecords]
      : forceRecords;
    const cacheKey = isEncoderSensorType(sensorType)
      ? `${sensorType}:${highPassEncoders}`
      : sensorType;
    const storedView = sensorRenderCacheRef.current.get(cacheKey);
    const cachedView =
      storedView?.sourceBuffer === labBuffer ? storedView : undefined;
    const nextHistogramDomain: Tuple<number> =
      cachedView?.histogramDomain ??
      (scalarRecords
        ? recordHistogramDomain(scalarRecords)
        : [SENSOR_VALUE_MIN, SENSOR_VALUE_MAX]);
    setHistogramDomain(nextHistogramDomain);

    const dataWithZ = interpolateZValuesForImageTimestamps(
      motionBin,
      imageDataArray
    );
    setImageDataWithZ(dataWithZ);

    const toolForceRenderData =
      cachedView?.renderData ??
      constructSensorRenderData(
        motionBin,
        toolForceRecords,
        glInfo,
        nextHistogramDomain,
        isLabSensorType(sensorType)
      );
    if (!cachedView) {
      sensorRenderCacheRef.current.set(cacheKey, {
        histogramDomain: nextHistogramDomain,
        renderData: toolForceRenderData,
        sourceBuffer: labBuffer,
      });
    }

    const {
      zRange,
      extrudedZRange,
      sensorValueRange,
      extrudedSensorValueRange,
      fullSensorValueRange,
      fullExtrudedSensorValueRange,
      sensorHistogram,
      extrudedSensorHistogram,
    } = toolForceRenderData.info;

    // Keep the display settings when the filter rebuilds the sensor values.
    const previousView = atomStore.get(viewAtom);
    const previousControls =
      previousView?.mode === "analyze" && previousView.id === attempt.id
        ? atomStore.get(previousView.controlsAtom)
        : undefined;
    const showNonExtrusionMotion =
      previousControls?.showNonExtrusionMotion ?? false;
    const initialRange = showNonExtrusionMotion
      ? sensorValueRange
      : extrudedSensorValueRange;
    const initialHistogram = showNonExtrusionMotion
      ? sensorHistogram
      : extrudedSensorHistogram;
    const initialFullRange = showNonExtrusionMotion
      ? fullSensorValueRange
      : fullExtrudedSensorValueRange;
    const minimumSensorRangeWidth = isLabSensorType(sensorType)
      ? (nextHistogramDomain[1] - nextHistogramDomain[0]) /
        SENSOR_HISTOGRAM_BIN_COUNT
      : sensorType === "toolForce"
        ? 0.1
        : 0.0001;

    const cAtom = atom<PrintViewControls>({
      zRange: {
        value: previousControls?.zRange.value ?? extrudedZRange,
        extrudedRange: extrudedZRange,
        range: zRange,
      },
      valueRange: {
        value: initialRange,
        extrudedSuggestedRange: extrudedSensorValueRange,
        suggestedRange: sensorValueRange,
        fullRange: fullSensorValueRange,
        fullExtrudedRange: fullExtrudedSensorValueRange,
        histogram: sensorHistogram,
        extrudedHistogram: extrudedSensorHistogram,
      },
      colorRange: {
        value: constrainRange(
          getCentralHistogramRange(
            initialHistogram,
            initialFullRange,
            nextHistogramDomain
          ),
          initialRange,
          Math.min(minimumSensorRangeWidth, initialRange[1] - initialRange[0])
        ),
      },
      lineWidth: previousControls?.lineWidth ?? {
        range: [0, 1.2],
        value: 0.25,
      },
      showTool0: previousControls?.showTool0 ?? true,
      showTool1: previousControls?.showTool1 ?? true,
      showNonExtrusionMotion,
      hideStaleDataAlert: previousControls?.hideStaleDataAlert ?? false,
      staleData: null,
    });

    setView({
      mode: "analyze",
      id: attempt.id,
      data: toolForceRenderData,
      controlsAtom: cAtom,
    });

    setControlsAtom(cAtom);
  }, [
    sensorType,
    encoderBuffer,
    labBuffer,
    scalarRecords,
    highPassEncoders,
    encoderQuery?.data,
    motionDataBuffer,
    toolForce0Buffer,
    toolForce1Buffer,
    attempt.id,
    setView,
    imageDataArray,
    glInfo,
    hasToolForce0,
    hasToolForce1,
    atomStore,
  ]);

  const isProcessing = !controlsAtom && !isDataFetching && !loadingError;
  const canShowControls =
    Boolean(controlsAtom) && !isDataFetching && Boolean(machine);

  // Publish lightweight overlay updates without rebuilding the 3D view data.
  useEffect(() => {
    if (!controlsAtom) {
      return undefined;
    }

    const staleData = canShowControls
      ? {
          dataUpdatedAt: motionDataQueryResult.dataUpdatedAt,
          statusTimestamp: attempt.endedAt ?? statusTimestamp,
          onRefresh: refreshData,
        }
      : null;
    atomStore.set(controlsAtom, (controls) => ({ ...controls, staleData }));

    return () =>
      atomStore.set(controlsAtom, (controls) => ({
        ...controls,
        staleData: null,
      }));
  }, [
    controlsAtom,
    canShowControls,
    attempt.endedAt,
    statusTimestamp,
    motionDataQueryResult.dataUpdatedAt,
    refreshData,
    atomStore,
  ]);

  if (!controlsAtom || isDataFetching) {
    return (
      <ViewerOverlayNotice
        title={t`components.controls.analyze.preparing-analyze-view`}
      >
        <S.ProgressWrapper>
          <ProgressBar
            title={t`components.controls.analyze.motion-data`}
            value={motionDataProgress}
          />
          {isEncoderSensorType(sensorType) && (
            <ProgressBar
              title={t`components.controls.analyze.encoder-data`}
              value={encoderProgress}
            />
          )}
          {isLabSensorType(sensorType) && (
            <ProgressBar title={sensorType} value={encoderProgress} />
          )}
          {toolForceBars
            .filter(({ show }) => show)
            .map(({ title, value }) => (
              <ProgressBar key={title} title={title} value={value} />
            ))}

          {loadingError && t`components.controls.loading-error`}
        </S.ProgressWrapper>
        {isProcessing && (
          <S.SpinnerSection>
            <Spinner size={28} />
            {t`components.controls.analyze.finalizing-3d-display-message`}
          </S.SpinnerSection>
        )}
      </ViewerOverlayNotice>
    );
  }

  if (!machine) {
    return <></>;
  }

  return (
    <SensorDataControls
      controlsAtom={controlsAtom}
      statusTimestamp={statusTimestamp}
      sensorTypes={sensorTypes}
      sensorType={sensorType}
      histogramDomain={histogramDomain}
      highPassEncoders={highPassEncoders}
      imageDataWithZ={imageDataWithZ}
      attempt={attempt}
      refreshData={refreshData}
      refreshAttempt={refreshAttempt}
      onCommandModalOpenChange={onCommandModalOpenChange}
      onSensorTypeChange={setSensorType}
      onHighPassEncodersChange={setHighPassEncoders}
      timelapseId={timelapseId}
      timelapseGenerating={timelapseGenerating}
      machine={machine}
    />
  );
};

/**
 * Poll for a timelapse if a print completed successfully recently but the
 * timelapse is not yet available. Give up polling 5 minutes after completion.
 */
function shouldPollForTimelapse(attempt: Attempt | undefined | null): boolean {
  const withinFiveMinutesOfCompletion =
    attempt?.endedAt != null &&
    Temporal.Instant.compare(
      Temporal.Now.instant(),
      Temporal.Instant.from(attempt.endedAt).add({ minutes: 5 })
    ) < 0;

  return (
    attempt != null &&
    !attempt.timelapseId &&
    attempt.status === "complete" &&
    withinFiveMinutesOfCompletion
  );
}

/**
 * Custom hook to load the images of an attempt.
 *
 * @param attempt - Attempt to load images for.
 * @param maxEndTime - RFC 3339 string representing the latest time to load data for this attempt.
 * @returns react-query result containing an array of `{ timestamp: number; id:
 * Uuid }` objects where the timestamp is the epoch time in microseconds when
 * the image was taken, and the id is the image's `id`. The
 * array is sorted in ascending order by timestamp.
 */
function useAttemptImages(attempt: Attempt, maxEndTime: string): ImageData[] {
  const firstImageEventTimestamp =
    Temporal.Instant.from(attempt.startedAt).epochMilliseconds * 1000;
  const lastImageEventTimestamp = attempt.endedAt
    ? Temporal.Instant.from(attempt.endedAt).epochMilliseconds * 1000
    : Temporal.Instant.from(maxEndTime).epochMilliseconds * 1000;

  const { data: images } = useMachineImages(
    {
      from: firstImageEventTimestamp,
      to: lastImageEventTimestamp,
      machineId: attempt.machineId,
      limit: 10_000, // enough for a 4-day print at 1 print per 30s
    },
    { staleTime: Infinity }
  );

  return useMemo(
    () =>
      images
        ? images
            .map((machineImage) => ({
              timestamp: machineImage.capturedAt,
              id: machineImage.image.id,
            }))
            .toReversed()
        : [],
    [images]
  );
}

export const AnalyzeViewerControlPanel = ({
  project,
}: {
  project: Project;
}): ReactElement => {
  const attemptId = useAnalyzeRoute()?.attemptId;

  return (
    <AnalyzeAttemptControlPanel
      key={attemptId}
      project={project}
      attemptId={attemptId}
    />
  );
};

/**
 * Top of three layers of components used to render an attempt. Layers
 * are: (from top to bottom)
 * - `AnalyzeAttemptControlPanel`: loads the attempt and its manifest, and
 *   handles error reporting when no data is found.
 * - `PrintAttemptLoader`: Loads the large data for a specific attempt,
 *   including motion, tool force, and image sensor data. Then transforms that
 *   data into a format suitable for the viewer.
 * - `SensorDataControls`: Renders the UI controls for the sensor data viewer
 *   and handles user interaction to update the view controls.
 */
const AnalyzeAttemptControlPanel = ({
  project,
  attemptId,
}: {
  project: Project;
  attemptId: Uuid | undefined;
}): ReactElement => {
  const isAonUser = useIsAonUser();
  const encodersAnalyzeFlag = useServerFeatureFlag("encoders-analyze-ui");
  const additionalSensorsEnabled = encodersAnalyzeFlag.data ?? false;

  const projectAttemptsResult = useProjectAttempts(project.id);
  const { data: attempts } = projectAttemptsResult;

  const originalAttempt = attempts?.find((a) => a.id === attemptId);

  const manifestResult = useAttemptDatasetManifest(
    { projectId: project.id, attempt: originalAttempt },
    { refetchOnMount: "always" }
  );
  const manifest = manifestResult.data?.manifest;

  // Keep active attempt status current, then poll for the timelapse after
  // completion. Pausing or resuming leaves the large print datasets loaded.
  const polledAttemptResult = usePolledAttempt(project, originalAttempt, {
    refetchInterval: (query) => {
      const attempt = query.state.data;
      return attempt?.status === "printing" ||
        attempt?.status === "paused" ||
        shouldPollForTimelapse(attempt)
        ? 5000
        : false;
    },
    refetchOnWindowFocus: true,
  });
  // Observe poll timestamps too, so print time advances even when react-query
  // preserves the unchanged attempt record's identity.
  const {
    data: polledAttempt,
    dataUpdatedAt,
    refetch: refetchAttempt,
  } = polledAttemptResult;

  const attempt = polledAttempt ?? originalAttempt;
  const statusTimestamp =
    dataUpdatedAt > 0
      ? Temporal.Instant.fromEpochMilliseconds(dataUpdatedAt).toString()
      : Temporal.Now.instant().toString();

  const [endTimestamp, setEndTimestamp] = useState(
    attempt?.endedAt ?? Temporal.Now.instant().toString()
  );

  const refreshPrintData = useCallback(() => {
    setEndTimestamp(attempt?.endedAt ?? Temporal.Now.instant().toString());
    void projectAttemptsResult.refetch();
    void manifestResult.refetch();
    // Refetch functions are always current because they come from react-query,
    // so it's safe to leave them out of the dependency array. (And actually
    // required to avoid infinitely re-rendering `PrintAttemptLoader`.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const handleRefreshData = useCallback(async () => {
    const { data: refreshedAttempt } = await refetchAttempt();
    // A transition to an ended status triggers the automatic refresh below.
    if (
      !refreshedAttempt ||
      !hasAttemptEnded(refreshedAttempt.status) ||
      refreshedAttempt.status === attempt?.status
    ) {
      refreshPrintData();
    }
  }, [attempt?.status, refetchAttempt, refreshPrintData]);

  const refreshData = useCallback(() => {
    void handleRefreshData();
  }, [handleRefreshData]);

  const handleRefreshAttempt = useCallback(async () => {
    await polledAttemptResult.refetch();
    // Refetch functions are always current because they come from react-query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isCommandModalOpen, setIsCommandModalOpen] = useState(false);
  const prevAttemptStatusRef = useRef(attempt?.status);

  // Refresh data automatically only when the attempt ends.
  useEffect(() => {
    // Keep the command modal mounted until the user closes it.
    if (isCommandModalOpen) {
      return;
    }

    if (
      attempt &&
      hasAttemptEnded(attempt.status) &&
      prevAttemptStatusRef.current !== undefined &&
      attempt.status !== prevAttemptStatusRef.current
    ) {
      refreshPrintData();
    }
    prevAttemptStatusRef.current = attempt?.status;
  }, [attempt, refreshPrintData, isCommandModalOpen]);

  if (projectAttemptsResult.isLoading) {
    return (
      <ViewerOverlayNotice
        title={t`components.controls.analyze.preparing-analyze-view`}
      >
        <S.SpinnerSection>
          <Spinner size={40} />
          {t`components.controls.analyze.loading-print-data`}
        </S.SpinnerSection>
      </ViewerOverlayNotice>
    );
  }
  if (!attempt) {
    return (
      <ViewerOverlayNotice
        title={t`components.controls.analyze.no-attempts-title`}
      >{t`components.controls.analyze.no-attempts-message`}</ViewerOverlayNotice>
    );
  }
  if (
    !manifest ||
    manifestResult.data?.isPolling ||
    manifestResult.isLoading ||
    encodersAnalyzeFlag.isLoading
  ) {
    return (
      <ViewerOverlayNotice
        title={t`components.controls.analyze.preparing-analyze-view`}
      >
        <S.SpinnerSection>
          <Spinner size={40} />
          {t`components.controls.analyze.loading-print-data`}
        </S.SpinnerSection>
      </ViewerOverlayNotice>
    );
  }
  if (
    !manifestResult.data?.isMinimalViewableManifest ||
    getAvailableSensorTypes(manifest, additionalSensorsEnabled).length === 0
  ) {
    return (
      <ViewerOverlayNotice
        title={t`components.controls.analyze.no-viewable-data-title`}
      >{t`components.controls.analyze.no-viewable-data-message`}</ViewerOverlayNotice>
    );
  }

  const motionDataId = manifest.find((m) => m.kind === "motionData")?.id;
  const toolForce0Id = manifest.find((m) => m.kind === "toolForce0")?.id;
  const toolForce1Id = manifest.find((m) => m.kind === "toolForce1")?.id;
  const encoderDataId = manifest.find(
    (m) =>
      additionalSensorsEnabled && m.kind === "axesEncoders" && m.samples > 0
  )?.id;

  if (!motionDataId) {
    return <></>;
  }

  const canViewTimelapse = attempt.status === "complete" || isAonUser;

  return (
    <PrintAttemptLoader
      statusTimestamp={statusTimestamp}
      key={attempt.id}
      projectId={project.id}
      attempt={attempt}
      timelapseId={canViewTimelapse ? attempt.timelapseId : undefined}
      timelapseGenerating={shouldPollForTimelapse(attempt)}
      motionDataId={motionDataId}
      toolForce0Id={toolForce0Id}
      toolForce1Id={toolForce1Id}
      encoderDataId={encoderDataId}
      manifest={manifest}
      additionalSensorsEnabled={additionalSensorsEnabled}
      endTimestamp={endTimestamp}
      refreshData={refreshData}
      refreshAttempt={handleRefreshAttempt}
      onCommandModalOpenChange={setIsCommandModalOpen}
    />
  );
};
